import { useEffect, useRef, useState } from "react";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { friendlyAuthError } from "@/lib/authErrors";
import { cn } from "@/lib/utils";

interface CheckInboxProps {
  /** Email the verification/reset link was sent to */
  email: string;
  /** What kind of email was sent */
  kind: "signup" | "reset";
  /** Called when user clicks "Use a different email" */
  onChangeEmail: () => void;
  /** Called when user wants to go back to sign-in */
  onBackToSignIn?: () => void;
  /** Compact mode for modals */
  compact?: boolean;
}

const RESEND_COOLDOWN_S = 45;

const CheckInbox = ({
  email,
  kind,
  onChangeEmail,
  onBackToSignIn,
  compact,
}: CheckInboxProps) => {
  const { resendVerificationEmail, sendPasswordReset } = useAuth();
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_S);
  const [resending, setResending] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setFeedback(null);
    const { error } =
      kind === "signup"
        ? await resendVerificationEmail(email)
        : await sendPasswordReset(email);
    setResending(false);
    if (error) {
      setFeedback({ type: "error", message: friendlyAuthError(error.message, "resend") });
      return;
    }
    setFeedback({ type: "success", message: "Email sent. Please check your inbox." });
    setCooldown(RESEND_COOLDOWN_S);
  };

  const isReset = kind === "reset";

  return (
    <div className={cn("w-full", compact ? "" : "max-w-md mx-auto")}>
      <div className="text-center">
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-sage/15 text-sage mb-3">
          <Mail className="h-6 w-6" />
        </div>
        <h2 className={cn("font-display text-foreground mb-2", compact ? "text-2xl" : "text-3xl md:text-4xl")}>
          Check your inbox
        </h2>
        <p className="text-muted-foreground text-sm">
          {isReset
            ? "We sent a link to reset your password."
            : "We sent a link to verify your email."}
        </p>
      </div>

      <div className="mt-5 rounded-xl border border-sage/30 bg-sage/5 p-4 text-center">
        <div className="flex items-center justify-center gap-2 text-sage mb-1">
          <CheckCircle2 className="h-4 w-4" />
          <span className="text-xs font-medium uppercase tracking-wide">Sent to</span>
        </div>
        <p className="text-sm text-foreground font-medium break-all">{email}</p>
      </div>

      <ol className="mt-5 space-y-2 text-sm text-muted-foreground">
        <li className="flex gap-2">
          <span className="font-medium text-foreground">1.</span>
          Open the email from us (check your spam folder if you don't see it).
        </li>
        <li className="flex gap-2">
          <span className="font-medium text-foreground">2.</span>
          {isReset
            ? "Click the link to choose a new password."
            : "Click the verification link to continue."}
        </li>
      </ol>

      {feedback && (
        <div
          role={feedback.type === "error" ? "alert" : "status"}
          className={cn(
            "mt-4 rounded-lg px-3 py-2 text-sm text-center",
            feedback.type === "error"
              ? "border border-destructive/30 bg-destructive/10 text-destructive"
              : "border border-sage/30 bg-sage/10 text-sage",
          )}
        >
          {feedback.message}
        </div>
      )}

      <div className="mt-5 flex flex-col gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={handleResend}
          disabled={cooldown > 0 || resending}
          className="w-full rounded-full"
        >
          {resending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {cooldown > 0 ? `Resend email in ${cooldown}s` : "Resend email"}
        </Button>

        <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
          <button
            type="button"
            onClick={onChangeEmail}
            className="hover:text-foreground underline-offset-2 hover:underline"
          >
            Change email
          </button>
          {onBackToSignIn && (
            <>
              <span className="opacity-40">•</span>
              <button
                type="button"
                onClick={onBackToSignIn}
                className="hover:text-foreground underline-offset-2 hover:underline"
              >
                Back to sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckInbox;
